import React, { useState } from "react";
import { Dialog } from "@progress/kendo-react-dialogs";
import { Upload, UploadOnAddEvent, UploadFileInfo } from "@progress/kendo-react-upload";
import { Button } from "@progress/kendo-react-buttons";
import { ProgressBar } from "@progress/kendo-react-progressbars";
import { SvgIcon } from "@progress/kendo-react-common";
import { cancelIcon, xIcon } from "@progress/kendo-svg-icons";
import { useInjection } from "@presentation/components/dependencyInjection";
import { UploadLibraryService } from "@application/useCases/library/UploadLibraryService";
import { useNotifications } from "@presentation/components/notifications";
import { useUploadStore } from "@presentation/stores/UploadStore";

interface UploadLibraryDialogProps {
    onClose: () => void;
    onUploadSuccess?: () => void;
}

export const UploadLibraryDialog: React.FC<UploadLibraryDialogProps> = ({ onClose }) => {
    const [files, setFiles] = useState<UploadFileInfo[]>([]);
    const uploadLibraryService = useInjection(UploadLibraryService);
    const { pushNotification } = useNotifications();
    const { activeUploads, cancelUpload, removeUpload } = useUploadStore();

    const libraryUploads = Object.values(activeUploads)
        .filter(u => u.type === "library")
        .sort((a, b) => {
            // Primary sort: Progress (descending)
            const progressDiff = b.progress.percent - a.progress.percent;
            if (progressDiff !== 0) return progressDiff;
            // Secondary sort: ID (descending) as a stable fallback
            return b.id.localeCompare(a.id);
        });

    const onAdd = (event: UploadOnAddEvent) => {
        setFiles(event.affectedFiles);
    };

    const handleUpload = async () => {
        if (files.length === 0) return;

        // Clear files from the input immediately so user sees them move to the list below
        const filesToUpload = [...files];
        setFiles([]);

        try {
            // Start all uploads
            // We don't await the entire batch here because we want to let them run in background
            // and the UI updates via the store.
            // However, we might want to trigger 'onUploadSuccess' if ALL of them complete successfully.
            // Since this is fire-and-forget from the dialog's perspective (it can be closed),
            // onUploadSuccess might be less relevant for the specific batch, but we can call it 
            // if we want to refresh the library list immediately.
            
            // For now, let's just launch them.
            for (const fileInfo of filesToUpload) {
                const file = fileInfo.getRawFile!();
                if (!file) continue;
                
                // We launch them asynchronously without awaiting each one individually to block the loop
                // but we catch start errors.
                uploadLibraryService.uploadFile(file, "externalLibraries")
                    .catch(error => {
                        console.error(`Failed to start upload for ${file.name}`, error);
                    });
            }

            pushNotification("Uploads started in background.", "info");
            
            // If the user passed a success callback, we might want to call it later, 
            // but for background uploads, usually the parent component refreshes on interval or listens to store.
            // For this specific dialog, we just leave it open or let user close it.

        } catch (error: any) {
            console.error("Upload initiation failed", error);
            pushNotification("Failed to start uploads.", "error");
        }
    };

    const isFinished = (status: string) => ["completed", "failed", "cancelled"].includes(status);

    return (
        <Dialog title="Upload Libraries" onClose={onClose} width={600}>
            <div style={{ padding: "20px" }}>
                <div style={{ marginBottom: "20px" }}>
                    <p>Select .kit or .speclib files to upload.</p>
                    <Upload
                        batch={false}
                        multiple={true}
                        autoUpload={false}
                        files={files}
                        onAdd={onAdd}
                        onRemove={(e) => {
                            setFiles(e.affectedFiles);
                        }}
                        showActionButtons={false}
                    />
                </div>

                {libraryUploads.length > 0 && (
                    <div style={{ marginBottom: "20px" }}>
                        <label style={{ display: "block", marginBottom: "10px", fontWeight: "bold" }}>Upload Status:</label>
                        <div style={{ maxHeight: "250px", overflowY: "auto", border: "1px solid #eee", padding: "10px", borderRadius: "4px" }}>
                            {libraryUploads.map(upload => (
                                <div key={upload.id} style={{ marginBottom: "5px", paddingBottom: "5px", borderBottom: "1px solid #f5f5f5" }}>
                                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", fontSize: "0.85rem", marginBottom: "5px" }}>
                                        <div style={{ flex: 1, marginRight: "10px", minWidth: 0 }}>
                                            <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontWeight: "bold" }} title={upload.fileName}>
                                                {upload.fileName}
                                            </div>
                                        </div>
                                        <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                                            <span style={{ color: upload.status === "failed" ? "red" : upload.status === "completed" ? "green" : "inherit", fontWeight: "bold" }}>
                                                {upload.status === "completed" ? "Completed" : 
                                                 upload.status === "failed" ? "Failed" : 
                                                 upload.status === "cancelled" ? "Cancelled" : 
                                                 `${Math.round(upload.progress.percent)}%`}
                                            </span>

                                            {(upload.status === "uploading" || upload.status === "pending") && (
                                                <Button
                                                    fillMode="flat"
                                                    title="Cancel Upload"
                                                    onClick={() => cancelUpload(upload.id)}
                                                >
                                                    <SvgIcon icon={cancelIcon} />
                                                </Button>
                                            )}

                                            {isFinished(upload.status) && (
                                                <Button
                                                    fillMode="flat"
                                                    title="Remove from list"
                                                    onClick={() => removeUpload(upload.id)}
                                                >
                                                    <SvgIcon icon={xIcon} />
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                    {upload.status === "failed" && upload.error && (
                                        <div style={{ fontSize: "0.75rem", color: "red", marginTop: "2px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={upload.error}>
                                            Error: {upload.error}
                                        </div>
                                    )}
                                    <ProgressBar 
                                        value={upload.progress.percent} 
                                        labelVisible={false} 
                                        style={{ height: "5px", borderRadius: "3px", marginTop: "2px", marginBottom: "2px" }} 
                                    />
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
                    <Button onClick={onClose}>Close</Button>
                    <Button 
                        themeColor="primary" 
                        onClick={handleUpload} 
                        disabled={files.length === 0}
                    >
                        Start Upload
                    </Button>
                </div>
            </div>
        </Dialog>
    );
};
